import React, { useState } from "react";
import ReactDOM from "react-dom";
import api from "./api";
import { useWhatsApp } from "../context/WhatsAppContext";
import useAuth from "../hooks/useAuth";

export const TOPIC_OPTIONS = [
  "Appointment Confirmed", "Rescheduling", "Pain Update",
  "Progress Good", "Dropping Out Risk", "Payment Due",
  "Exercise Compliance", "General Check-in", "No Answer", "Left Voicemail",
  "Needs Motivation", "Travel / Busy", "Injury / Setback",
];

export const ESCALATE_OPTIONS = ["None", "Doctor", "Admin", "Other Therapist"];

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4,
};

const inputStyle = {
  width: "100%", padding: "8px 10px", borderRadius: 8, border: "1.5px solid #d1d5db",
  fontSize: 13, boxSizing: "border-box", outline: "none",
};

/* ───────────────────────────────────────────────
   CallLogModal
   Props:
     patient   — object with .name (displayed in header)
     calllead  — the calllead/lead doc with ._id, .followUpHistory, .messageHistory
     mode      — 'call' | 'whatsapp'
     phone     — phone number string
     onClose   — called when modal should close
     onSaved   — called with the updates object after successful API save
─────────────────────────────────────────────── */
export default function CallLogModal({ patient, calllead, mode, phone, onClose, onSaved }) {
  const { sendWhatsApp, config: waConfig } = useWhatsApp();
  const { auth } = useAuth();
  const [topics, setTopics] = useState([]);
  const [summary, setSummary] = useState("");
  const [nextAppt, setNextAppt] = useState("");
  const [nextFollowup, setNextFollowup] = useState("");
  const [stopFollowup, setStopFollowup] = useState(false);
  const [escalateTo, setEscalateTo] = useState("None");
  const [comments, setComments] = useState("");
  const [waText, setWaText] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleTopic = (t) =>
    setTopics(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const doAction = async () => {
    if (mode === "call") {
      window.location.href = `tel:${phone}`;
    } else {
      const txt = waText.trim()
        ? `Namaste ${patient.name} Ji! ${waText.trim()}`
        : `Namaste ${patient.name} Ji!`;
      await sendWhatsApp(phone, txt);
    }
  };

  const handleSaveAndAct = async () => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const logEntry = {
        date: now,
        type: mode === "call" ? "Call Log" : "WhatsApp Log",
        mode,
        summary,
        topics,
        nextAppt,
        nextFollowup,
        stopFollowup,
        escalateTo: escalateTo !== "None" ? escalateTo : "",
        comments,
        loggedBy: auth?.username || "",
        updatedAt: now,
      };

      if (calllead?._id) {
        const updates = {
          lastContactedDate: now,
          followUpHistory: [...(calllead.followUpHistory || []), {
            date: now,
            comments: [summary, ...topics].filter(Boolean).join(" | "),
            enquiryDetails: comments,
            ...logEntry,
          }],
        };

        if (mode === "whatsapp" && waText.trim()) {
          const msgText = `Namaste ${patient.name} Ji! ${waText.trim()}`;
          updates.messageHistory = [...(calllead.messageHistory || []), {
            date: now,
            type: "Patient Communication",
            text: msgText,
          }];
          updates.lastMessageDate = now;
          updates.lastMessageType = "Patient Communication";
        }

        if (nextFollowup) updates.followUpDate = new Date(nextFollowup).toISOString();
        if (stopFollowup) updates.followUpDate = null;

        await api.post(
          process.env.REACT_APP_URL + `/callleads/profiles/update/${calllead._id}`,
          updates
        );
        onSaved && onSaved(updates);
      }

      doAction();
      onClose();
    } catch (err) {
      console.error("Log save error", err);
      doAction();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOnly = async () => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const logEntry = {
        date: now,
        type: mode === "call" ? "Call Log" : "WhatsApp Log",
        mode,
        summary,
        topics,
        nextAppt,
        nextFollowup,
        stopFollowup,
        escalateTo: escalateTo !== "None" ? escalateTo : "",
        comments,
        loggedBy: auth?.username || "",
        updatedAt: now,
      };

      if (calllead?._id) {
        const updates = {
          lastContactedDate: now,
          followUpHistory: [...(calllead.followUpHistory || []), {
            date: now,
            comments: [summary, ...topics].filter(Boolean).join(" | "),
            enquiryDetails: comments,
            ...logEntry,
          }],
        };

        if (mode === "whatsapp" && waText.trim()) {
          const msgText = `Namaste ${patient.name} Ji! ${waText.trim()}`;
          updates.messageHistory = [...(calllead.messageHistory || []), {
            date: now,
            type: "Patient Communication",
            text: msgText,
          }];
          updates.lastMessageDate = now;
          updates.lastMessageType = "Patient Communication";
        }

        if (nextFollowup) updates.followUpDate = new Date(nextFollowup).toISOString();
        if (stopFollowup) updates.followUpDate = null;

        await api.post(
          process.env.REACT_APP_URL + `/callleads/profiles/update/${calllead._id}`,
          updates
        );
        onSaved && onSaved(updates);
      }

      onClose();
    } catch (err) {
      console.error("Log save error", err);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isWA = mode === "whatsapp";
  const accentColor = isWA ? "#25D366" : "#1976d2";
  const modeLabel = isWA ? "WhatsApp" : "Call";
  const modeIcon = isWA ? "💬" : "📞";

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999,
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 12, width: 520, maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: accentColor, color: "#fff", padding: "14px 20px",
          borderRadius: "12px 12px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{modeIcon} Log {modeLabel} — {patient.name}</div>
            <div style={{ fontSize: 12, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 8 }}>
              📱 {phone}
              {isWA && waConfig.provider !== 'manual' && (
                <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                  via {waConfig.provider.toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Topic chips */}
          <div>
            <label style={labelStyle}>What was discussed? (multi-select)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {TOPIC_OPTIONS.map(t => (
                <button
                  key={t}
                  onClick={() => toggleTopic(t)}
                  style={{
                    padding: "4px 10px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                    border: `1.5px solid ${topics.includes(t) ? accentColor : "#d1d5db"}`,
                    background: topics.includes(t) ? accentColor : "#f9fafb",
                    color: topics.includes(t) ? "#fff" : "#374151",
                    fontWeight: topics.includes(t) ? 700 : 400,
                    transition: "all 0.15s",
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div>
            <label style={labelStyle}>Conversation Summary</label>
            <input
              type="text"
              value={summary}
              onChange={e => setSummary(e.target.value)}
              placeholder="Brief summary of the conversation…"
              style={inputStyle}
            />
          </div>

          {/* WhatsApp message (WA mode only) */}
          {isWA && (
            <div>
              <label style={labelStyle}>
                WhatsApp Message Sent{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional — saved to message history)</span>
              </label>
              <textarea
                rows={3}
                value={waText}
                onChange={e => setWaText(e.target.value)}
                placeholder="Type the message you sent (or will send)…"
                style={{ ...inputStyle, resize: "vertical" }}
              />
              {waText.trim() && (
                <div style={{ marginTop: 6, padding: "8px 12px", background: "#f0fdf9", borderRadius: 8, fontSize: 12, color: "#1a4731" }}>
                  💬 Preview: <em>Namaste {patient.name} Ji! {waText.trim()}</em>
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>📅 Next Appointment</label>
              <input type="date" value={nextAppt} onChange={e => setNextAppt(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>⏰ Next Follow-up</label>
              <input
                type="date"
                value={nextFollowup}
                onChange={e => { setNextFollowup(e.target.value); setStopFollowup(false); }}
                style={inputStyle}
                disabled={stopFollowup}
              />
            </div>
          </div>

          {/* Stop follow-up + Escalate */}
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={stopFollowup}
                onChange={e => { setStopFollowup(e.target.checked); if (e.target.checked) setNextFollowup(""); }}
              />
              <span style={{ color: "#b71c1c", fontWeight: 600 }}>🛑 Stop Follow-up</span>
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#92400e" }}>⬆ Escalate to:</label>
              <select
                value={escalateTo}
                onChange={e => setEscalateTo(e.target.value)}
                style={{
                  padding: "4px 8px", borderRadius: 6, border: "1.5px solid #d1d5db", fontSize: 13,
                  background: escalateTo !== "None" ? "#fef3c7" : "#fff",
                }}
              >
                {ESCALATE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Comments */}
          <div>
            <label style={labelStyle}>Additional Notes / Comments</label>
            <textarea
              rows={3}
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="Any important notes, patient concerns, observations…"
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>

          {/* No calllead warning */}
          {!calllead && (
            <div style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", padding: "8px 12px", borderRadius: 8, border: "1px solid #fcd34d" }}>
              ⚠️ No KIT record found for this patient. Log will NOT be saved. You can still make the {modeLabel}.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", display: "flex", gap: 8, borderTop: "1px solid #e5e7eb" }}>
          <button
            onClick={handleSaveAndAct}
            disabled={saving}
            style={{
              flex: 2, padding: "10px 16px", background: accentColor, color: "#fff",
              border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            {saving ? "Saving…" : `💾 Save & ${isWA ? "Open WhatsApp" : "Call"}`}
          </button>
          <button
            onClick={handleSaveOnly}
            disabled={saving || !calllead}
            style={{
              flex: 1, padding: "10px 16px", background: "#f0fdf4", color: "#16a34a",
              border: "1.5px solid #86efac", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
              opacity: (saving || !calllead) ? 0.5 : 1,
            }}
          >
            {saving ? "Saving…" : "💾 Save Log"}
          </button>
          <button
            onClick={() => { doAction(); onClose(); }}
            style={{
              flex: 1, padding: "10px 16px", background: "#f3f4f6", color: "#374151",
              border: "1.5px solid #d1d5db", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            {modeIcon} Just {modeLabel}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "10px 16px", background: "#fff", color: "#6b7280",
              border: "1.5px solid #e5e7eb", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
